// Prep/cook times are canonically stored as a whole number of MINUTES. Everything that
// calculates (timing filters, plan card totals) reads that number; everything that displays
// derives its text from it via formatMinutes. Nothing computes from a raw input string.
//
// null means "not set", which is deliberately distinct from 0 — a no-cook recipe has a real
// zero cook time, and the two must not render the same.

export const NO_TIME_LABEL = 'Time not set';

export const hasTime = (minutes) => typeof minutes === 'number' && Number.isFinite(minutes);

// Parse a stored value or imported string into whole minutes; null when there is nothing
// usable. Imports feed this (URL/schema.org ISO durations, OCR text, pasted text), so it
// has to cope with "PT1H30M", "1 hr 30 mins", "1:30", "90 mins" and a bare "90".
export function toMinutes(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : null;

  const raw = String(value).trim().toLowerCase();
  if (!raw) return null;

  // ISO 8601 duration — what schema.org recipe markup emits for prepTime/cookTime.
  const iso = raw.match(/^p(?:\d+d)?t(?:(\d+(?:\.\d+)?)h)?(?:(\d+(?:\.\d+)?)m)?(?:(\d+(?:\.\d+)?)s)?$/);
  if (iso && (iso[1] || iso[2] || iso[3])) {
    return Math.round(Number(iso[1] || 0) * 60 + Number(iso[2] || 0) + Number(iso[3] || 0) / 60);
  }

  // h:mm
  const colon = raw.match(/^(\d+):([0-5]\d)$/);
  if (colon) return Number(colon[1]) * 60 + Number(colon[2]);

  // Otherwise scan number+unit pairs and classify each by its leading letter. A bare number
  // is minutes, which covers both "15" and the trailing half of "1 h 30".
  let hours = 0;
  let minutes = 0;
  let matched = false;
  const tokens = /(\d+(?:\.\d+)?)\s*([a-z]*)/g;
  let token;
  while ((token = tokens.exec(raw)) !== null) {
    const amount = Number(token[1]);
    if (!Number.isFinite(amount)) continue;
    const unit = token[2];
    if (unit.startsWith('h')) hours += amount;
    else if (unit.startsWith('m')) minutes += amount;
    else if (unit.startsWith('s')) minutes += amount / 60;
    else if (unit) continue; // unrecognised unit — ignore rather than guess
    else minutes += amount;
    matched = true;
  }

  if (!matched) return null;
  return Math.round(hours * 60 + minutes);
}

// Combined prep + cook, treating an unset half as 0. Null only when neither is set.
export function totalMinutes(prep, cook) {
  if (!hasTime(prep) && !hasTime(cook)) return null;
  return (hasTime(prep) ? prep : 0) + (hasTime(cook) ? cook : 0);
}

// The single number to filter and summarise on, in order of trustworthiness:
//   1. a COMPLETE prep+cook breakdown — the most specific thing we can know
//   2. a published total — beats half a breakdown, which would under-report (a recipe with
//      prep 10 and total 40 is a 40-minute recipe, not a 10-minute one)
//   3. whatever half of the breakdown we do have
// A total is never added to prep or cook: it already contains the cook time, and summing
// them is what made a 30-minute recipe read as 60.
export function effectiveTotalMinutes(recipe) {
  if (!recipe) return null;
  const { prepTime, cookTime, totalTime } = recipe;
  if (hasTime(prepTime) && hasTime(cookTime)) return prepTime + cookTime;
  if (hasTime(totalTime)) return totalTime;
  return totalMinutes(prepTime, cookTime);
}

// Minutes -> display text, in the largest sensible unit so an hour-long recipe reads as
// hours rather than "90m".
export function formatMinutes(minutes) {
  if (!hasTime(minutes)) return NO_TIME_LABEL;
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
}

// --- Structured (hours + minutes dropdown) support ---------------------------------------

const BASE_HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const BASE_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, i) => i * 5); // 0,5,..,55

// Off-grid values (an import handing us 37 minutes, or a 14-hour slow cook) get their exact
// value added as an option rather than being silently rounded onto the grid.
const withValue = (base, value) =>
  hasTime(value) && !base.includes(value) ? [...base, value].sort((a, b) => a - b) : base;

export const hourOptions = (current) => withValue(BASE_HOUR_OPTIONS, current);
export const minuteOptions = (current) => withValue(BASE_MINUTE_OPTIONS, current);

// Split canonical minutes into the two dropdown values. Blank (null) is used for a zero
// hours or zero trailing minutes so the controls stay uncluttered, but never for both at
// once when the value is a real 0 — that would be indistinguishable from "not set".
export function splitHoursMinutes(minutes) {
  if (!hasTime(minutes)) return { hours: null, mins: null };
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return { hours: null, mins };
  return { hours, mins: mins === 0 ? null : mins };
}

// Recombine the two dropdown values. Both blank means "not set".
export function fromHoursMinutes(hours, mins) {
  if (!hasTime(hours) && !hasTime(mins)) return null;
  return (hasTime(hours) ? hours : 0) * 60 + (hasTime(mins) ? mins : 0);
}
